/**
 * Builders de payload para criação de Persona (Brief 4 · Tela 2 · PR6).
 *
 * Espelha `playbook-create.ts` (Brief 4 · Tela 1). O form de criação envia só os
 * campos OBRIGATÓRIOS do `CreatePersonaDto` (name · vertical · os 3 objetos) — o
 * resto (examples/language/side/adversarial/metadata) recebe default no
 * schema/serviço, confirmado no R15 ao vivo (corpo mínimo nasce `status:"active"`,
 * `version:1`, `language:"pt-BR"`, `sideDefault:"neutral"`,
 * `adversarialProfileDefault:"none"`). Os 3 objetos podem nascer com arrays/strings
 * vazios — o Gestor ajusta os detalhes no editor logo em seguida.
 */

import type {
  CreatePersonaBody,
  PersonaEthicalBoundaries,
  PersonaVertical,
  PersonaVocabulary,
  PersonaVoiceProfile,
} from './types';

/** Verticais oferecidas no form de criação (espelham o enum do `CreatePersonaDto`). */
export const CREATE_VERTICALS: PersonaVertical[] = [
  'support',
  'commercial',
  'retention',
  'collections',
  'triage',
  'onboarding',
  'satisfaction_survey',
  'legal',
  'healthcare',
  'other',
];

export interface CreatePersonaFormErrors {
  name?: string;
}

/**
 * Valida os campos do form de criação contra as constraints do `CreatePersonaDto`.
 * Só `name` (2–120) é validável no client — os 3 objetos são `@IsObject` e podem
 * nascer vazios. Retorna um mapa de erros por campo (vazio = válido).
 */
export function validateCreatePersonaForm(fields: { name: string }): CreatePersonaFormErrors {
  const errors: CreatePersonaFormErrors = {};
  const name = fields.name.trim();

  if (name.length < 2) errors.name = 'Informe um nome com ao menos 2 caracteres.';
  else if (name.length > 120) errors.name = 'O nome deve ter no máximo 120 caracteres.';

  return errors;
}

/**
 * Monta o `CreatePersonaBody` a partir do form. `name` é trimado; o `voiceProfile`
 * tem suas 3 dimensões trimadas (podem ficar vazias — texto livre, sem enum); a
 * vocabulary/ethicalBoundaries entram como ARRAY puro (o backend grava o objeto
 * inteiro). Defaults do backend cobrem o restante (status active, language pt-BR).
 */
export function buildCreatePersonaPayload(fields: {
  name: string;
  vertical: PersonaVertical;
  voiceProfile: PersonaVoiceProfile;
  vocabulary: PersonaVocabulary;
  ethicalBoundaries: PersonaEthicalBoundaries;
}): CreatePersonaBody {
  return {
    name: fields.name.trim(),
    vertical: fields.vertical,
    voiceProfile: {
      formality: fields.voiceProfile.formality.trim(),
      warmth: fields.voiceProfile.warmth.trim(),
      pace: fields.voiceProfile.pace.trim(),
    },
    vocabulary: {
      preferredTerms: [...fields.vocabulary.preferredTerms],
      avoidedTerms: [...fields.vocabulary.avoidedTerms],
    },
    ethicalBoundaries: {
      redirectTopics: [...fields.ethicalBoundaries.redirectTopics],
      toneLimits: [...fields.ethicalBoundaries.toneLimits],
    },
  };
}
